import { useCallback, useEffect, useState } from "react";
import { Card, Descriptions, Typography, message } from "antd";
import ListTable from "common/ListTable";
import { useParams } from "react-router";
import { QC_VALIDATION_SHOW_ENDPOINT } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import { cglFormatDate } from "utils/functions";

const QcValidationReportView = () => {
  const { id } = useParams();

  const [validationData, setValidationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refresh, setRefresh] = useState(0);

  const getValidationDetails = useCallback(async () => {
    if (!id) {
      message.error("Missing required parameters to load QC Validation details.");
      return;
    }

    setLoading(true);
    setError(null);

    const response = await getData(`${QC_VALIDATION_SHOW_ENDPOINT}/${id}`, false);

    if (response?.status === 200 && response?.data?.code === 200) {
      setValidationData(response?.data?.data || null);
    } else {
      message.error(
        response?.data?.message || "Error getting QC Validation details",
      );
      setError(response?.data?.message || "Could not load this list.");
    }

    setLoading(false);
  }, [id]);

  useEffect(() => {
    getValidationDetails();
  }, [refresh, getValidationDetails]);

  const validationDetailColumns = [
    {
      title: "SL",
      key: "sl",
      width: 25,
      // align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Defect Categories",
      key: "defect_categories",
      render: (_, record) =>
        record?.defect
          ?.map((item) => item?.defect_category_name)
          .filter(Boolean)
          .join(", ") || "N/A",
    },
    {
      title: "Operations",
      key: "operations",
      render: (_, record) =>
        record?.operation
          ?.map((item) => item?.operation_name)
          .filter(Boolean)
          .join(", ") || "N/A",
    },
    {
      title: "Other Defect",
      dataIndex: "other_defect",
      key: "other_defect",
      render: (value) => value || "N/A",
    },
    {
      title: "Defect Qty",
      dataIndex: "defect_qty",
      key: "defect_qty",
      align: "right",
    },
  ];

  return (
    <AppContent
      header={
        <AppPageHeader
          extra={
            <ExtraContent
              refresh={refresh}
              setRefresh={setRefresh}
              hideAddButton={true}
              hideFilterButton={true}
              showBackButton
            />
          }
          title={
            <Typography.Title level={5}>
              QC Validation Report Details
            </Typography.Title>
          }
        />
      }
      content={
        <>
          <Card
            bordered
            loading={loading}
            style={{ margin: "10px 0" }}
            title={<Typography.Title level={5}>Summary</Typography.Title>}
          >
            <Descriptions column={4}>
              <Descriptions.Item label="Validation Date">
                {validationData?.validation_date
                  ? cglFormatDate(validationData.validation_date)
                  : "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Line">
                {validationData?.line_no || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="QC Name">
                {validationData?.qc_name || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Q.I Name">
                {validationData?.qi_name || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Q.I ID Card No">
                {validationData?.qi_id_card_no || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Check Points">
                {validationData?.check_points
                  ?.map((item) => item?.name)
                  .filter(Boolean)
                  .join(", ") || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Checked PCs">
                {validationData?.checked_pcs ?? "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Total Defect Qty">
                {validationData?.total_defect_qty ?? "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Defect Rate (%)">
                {validationData?.defect_rate != null
                  ? `${validationData.defect_rate}%`
                  : "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Corrective Action" span={2}>
                {validationData?.corrective_action || "N/A"}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card
            bordered
            loading={loading}
            title={
              <Typography.Title level={5}>Validation Details</Typography.Title>
            }
          >
            <ListTable
              bordered
              size="small"
              loading={loading}
              error={error}
              onRetry={() => getValidationDetails()}
              emptyText="No records to show yet."
              scroll={{x: "max-content"}}
              pagination={false}
              dataSource={validationData?.validation_details || []}
              columns={validationDetailColumns}
              rowKey={(record) => record?.id}
            />
          </Card>
        </>
      }
    />
  );
};

export default QcValidationReportView;
